import React, { useState, useEffect } from 'react';
import { useErrors } from '@contexts/ErrorsContext';
import { getErrorMessage } from '@utils/commons';

export interface ContactDetailsTypes {
  mobileNumber: string;
  homeNumber: string;
  email: string;
  preferredMethod: string;
}

interface ContactDetailsProps {
  contactDetails: ContactDetailsTypes;
  setContactDetails: (value: ContactDetailsTypes) => void;
}

const ContactDetails: React.FC<ContactDetailsProps> = ({
  contactDetails,
  setContactDetails,
}) => {
  const handleSelectMethod = (e: any) => {
    const selectedMethod = e.target.value;
    setContactDetails({ ...contactDetails, preferredMethod: selectedMethod });
  };
  const { errors } = useErrors();
  const [contactEmailErrorMessage, setContactEmailErrorMessage] = useState<
    string | undefined
  >('');

  useEffect(() => {
    setContactEmailErrorMessage(
      getErrorMessage(errors, 'preferredContactEmail')
    );
  }, [errors]);

  return (
    <div>
      <h1 className="govuk-heading-l mt-10 govuk-bp:mt-14">
        Person&apos;s contact details
      </h1>
      <div>
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="mobile-number">
            What is your mobile telephone number?
          </label>
          <input
            className="govuk-input govuk-input--width-20"
            id="mobile-number"
            name="mobileNumber"
            type="tel"
            autoComplete="tel"
            aria-describedby="having-conversations-phone-number"
            placeholder="Enter number"
            maxLength={11}
            value={contactDetails.mobileNumber ?? ''}
            onChange={e => {
              const { value } = e.target;
              if (/^[0-9]*$/.test(value) && value.length <= 11) {
                setContactDetails({
                  ...contactDetails,
                  mobileNumber: value,
                });
              }
            }}
          />
        </div>
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="home-number">
            What is your home telephone number?
          </label>
          <input
            className="govuk-input govuk-input--width-20"
            id="home-number"
            name="homeNumber"
            type="tel"
            autoComplete="tel"
            aria-describedby="having-conversations-phone-number"
            placeholder="Enter number"
            maxLength={11}
            value={contactDetails.homeNumber ?? ''}
            onChange={e => {
              const { value } = e.target;
              if (/^[0-9]*$/.test(value) && value.length <= 11) {
                setContactDetails({
                  ...contactDetails,
                  homeNumber: value,
                });
              }
            }}
          />
        </div>
        <div
          className={`govuk-form-group ${contactEmailErrorMessage ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="preferredContactEmail">
            What’s your preferred email address?
          </label>
          <input
            className={`govuk-input govuk-input--width-20 ${contactEmailErrorMessage ? 'govuk-input--error' : ''}`}
            id="preferredContactEmail"
            name="preferredContactEmail"
            type="preferredContactEmail"
            placeholder="Enter email address"
            spellCheck={false}
            aria-describedby={
              contactEmailErrorMessage ? 'email-input-error' : undefined
            }
            autoComplete="email"
            value={contactDetails.email ?? ''}
            onChange={e => {
              setContactDetails({ ...contactDetails, email: e.target.value });
            }}
          />
          {contactEmailErrorMessage && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {contactEmailErrorMessage}
            </p>
          )}
        </div>
      </div>
      <div className="govuk-form-group mt-10 govuk-bp:mt-14">
        <fieldset
          className="govuk-fieldset"
          aria-describedby="changedName-hint"
        >
          <legend className="govuk-fieldset__legend govuk-fieldset__legend--l">
            <h1 className="govuk-fieldset__heading">
              Preferred method of contact
            </h1>
          </legend>
          <div id="contact-hint" className="govuk-hint">
            What is the best way for us to contact you?
          </div>

          <div className="govuk-radios" data-module="govuk-radios">
            <div className="govuk-radios__item">
              <input
                className="govuk-radios__input"
                id="mobile_telephone"
                name="changedName"
                type="radio"
                value="mobile_telephone"
                checked={contactDetails.preferredMethod === 'mobile_telephone'}
                onChange={handleSelectMethod}
              />
              <label
                className="govuk-label govuk-radios__label"
                htmlFor="mobile_telephone"
              >
                Mobile telephone
              </label>
            </div>
            <div className="govuk-radios__item">
              <input
                className="govuk-radios__input"
                id="home_telephone"
                name="changedName"
                type="radio"
                value="home_telephone"
                checked={contactDetails.preferredMethod === 'home_telephone'}
                onChange={handleSelectMethod}
              />
              <label
                className="govuk-label govuk-radios__label"
                htmlFor="home_telephone"
              >
                Home telephone
              </label>
            </div>
            <div className="govuk-radios__item">
              <input
                className="govuk-radios__input"
                id="prefer_email"
                name="changedName"
                type="radio"
                value="prefer_email"
                checked={contactDetails.preferredMethod === 'prefer_email'}
                onChange={handleSelectMethod}
              />
              <label
                className="govuk-label govuk-radios__label"
                htmlFor="prefer_email"
              >
                Email address
              </label>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default ContactDetails;
